/**
 * The company's on-time rule, as edited and as described (DEC-326 item 5) — pure, tested.
 *
 *   ALLOWANCE  X (0–500)  late = more than X% slower than the route's typical time (median)
 *   PERCENTILE X (1–10)   late = slower than X out of 10 earlier loads on the route
 */

export type OnTimeMethod = 'ALLOWANCE' | 'PERCENTILE';

export interface OnTimeRule {
  readonly method: OnTimeMethod;
  readonly value: number;
}

export const RULE_RANGE: Record<OnTimeMethod, { readonly min: number; readonly max: number }> = {
  ALLOWANCE: { min: 0, max: 500 },
  PERCENTILE: { min: 1, max: 10 },
};

export type RuleParse =
  | { readonly ok: true; readonly rule: OnTimeRule }
  | { readonly ok: false; readonly message: string };

export function parseRule(method: OnTimeMethod, raw: string): RuleParse {
  const t = raw.trim();
  const { min, max } = RULE_RANGE[method];
  if (!/^\d{1,3}$/.test(t) || Number(t) < min || Number(t) > max) {
    return { ok: false, message: `Enter a whole number from ${String(min)} to ${String(max)}.` };
  }
  return { ok: true, rule: { method, value: Number(t) } };
}

/** One plain sentence for the office: what "on time" means under this rule. */
export function describeRule(rule: OnTimeRule): string {
  if (rule.method === 'ALLOWANCE') {
    return rule.value === 0
      ? "A load is on time if it's no slower than the route's typical time."
      : `A load is on time if it's no more than ${String(rule.value)}% slower than the route's typical time.`;
  }
  return `A load is on time if it's no slower than ${String(rule.value)} out of 10 earlier loads on the same route.`;
}
